// Son spatialisé (HRTF) : chaque bruit est placé dans le monde, plus faible et plus sourd au loin.

const FILES: Record<string, string> = {
  shot_pistol: 'shot_pistol.wav', shot_ar: 'shot_rifle.wav', shot_dmr: 'shot_rifle.wav', shot_sg: 'shot_shotgun.wav', shot_smg: 'shot_pistol.wav', shot_ak: 'shot_rifle.wav', shot_bolt: 'shot_rifle.wav', rack: 'shotgun_rack.mp3',
  reload_pistol: 'reload_pistol.wav', reload_ar: 'reload_rifle.wav', reload_dmr: 'reload_rifle.wav', reload_sg: 'shotgun_shell.mp3', reload_smg: 'reload_pistol.wav', reload_ak: 'reload_rifle.wav', reload_bolt: 'reload_rifle.wav',
  zombie_0: 'zombie_01.wav', zombie_1: 'zombie_03.wav', zombie_2: 'zombie_05.wav', zombie_3: 'zombie_07.wav', zombie_4: 'zombie_09.wav', zombie_5: 'zombie_11.wav',
  growl_0: 'zgrowl_1.ogg', growl_1: 'zgrowl_2.ogg', growl_2: 'zgrowl_3.ogg', growl_3: 'zgrowl_4.ogg',
  rummage_0: 'handleSmallLeather.ogg', rummage_1: 'dropLeather.ogg', latch: 'metalLatch.ogg',
  dry: 'metalClick.ogg', hit_0: 'impactPunch_heavy_000.ogg', hit_1: 'impactPunch_heavy_001.ogg',
  body_0: 'impactSoft_heavy_000.ogg', body_1: 'impactSoft_heavy_001.ogg',
  metal_0: 'impactMetal_light_000.ogg', metal_1: 'impactMetal_light_001.ogg',
  wood_0: 'impactWood_heavy_000.ogg', wood_1: 'impactWood_heavy_001.ogg',
  cloth_0: 'cloth1.ogg', cloth_1: 'cloth2.ogg', wind: 'wind.ogg',
  step_0: 'footstep_concrete_000.ogg', step_1: 'footstep_concrete_001.ogg', step_2: 'footstep_concrete_002.ogg', step_3: 'footstep_concrete_003.ogg', step_4: 'footstep_concrete_004.ogg',
};

export interface PlayOpts { x?: number; z?: number; vol?: number; rate?: number; ref?: number; max?: number; lowpass?: number }

export class Audio3D {
  ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfx: GainNode | null = null; // effets (tirs, pas, impacts, infectés)
  private amb: GainNode | null = null; // ambiance (vent, grillons, tirs lointains)
  private music: GainNode | null = null; // musique des menus
  private ui: GainNode | null = null; // sons de l'interface
  private volumes = { master: 0.8, sfx: 1, amb: 1, music: 0.7, ui: 0.8 };
  private buf = new Map<string, AudioBuffer>();
  private loading: Promise<void> | null = null;
  private wind: GainNode | null = null;

  init(): Promise<void> {
    if (this.loading) return this.loading;
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volumes.master;
    this.sfx = this.ctx.createGain();
    this.sfx.gain.value = this.volumes.sfx;
    this.amb = this.ctx.createGain();
    this.amb.gain.value = this.volumes.amb;
    this.music = this.ctx.createGain();
    this.music.gain.value = this.volumes.music;
    this.ui = this.ctx.createGain();
    this.ui.gain.value = this.volumes.ui;
    for (const b of [this.sfx, this.amb, this.music, this.ui]) b.connect(this.master);
    // Compresseur : les tirs proches ne saturent pas
    const comp = this.ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.ratio.value = 4;
    this.master.connect(comp).connect(this.ctx.destination);
    const ctx = this.ctx;
    this.loading = Promise.all(Object.entries(FILES).map(async ([k, f]) => {
      try {
        const r = await fetch(`assets/sfx/${f}`);
        this.buf.set(k, await ctx.decodeAudioData(await r.arrayBuffer()));
      } catch { /* son manquant : ignoré */ }
    })).then(() => undefined);
    return this.loading;
  }

  // Volumes de 0 à 1, appliqués en douceur (pas de claquement)
  setVolumes(v: { master: number; sfx: number; amb: number; music: number; ui: number }) {
    this.volumes = { ...v };
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.master?.gain.setTargetAtTime(v.master, t, 0.05);
    this.sfx?.gain.setTargetAtTime(v.sfx, t, 0.05);
    this.amb?.gain.setTargetAtTime(v.amb, t, 0.05);
    this.music?.gain.setTargetAtTime(v.music, t, 0.05);
    this.ui?.gain.setTargetAtTime(v.ui, t, 0.05);
  }

  resume() { if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume(); }

  listen(x: number, z: number, yaw: number) {
    if (!this.ctx) return;
    const L = this.ctx.listener, t = this.ctx.currentTime;
    if (L.positionX) {
      L.positionX.setValueAtTime(x, t); L.positionY.setValueAtTime(1.6, t); L.positionZ.setValueAtTime(z, t);
      L.forwardX.setValueAtTime(-Math.sin(yaw), t); L.forwardY.setValueAtTime(0, t); L.forwardZ.setValueAtTime(-Math.cos(yaw), t);
      L.upX.setValueAtTime(0, t); L.upY.setValueAtTime(1, t); L.upZ.setValueAtTime(0, t);
    }
  }

  play(name: string, o: PlayOpts = {}) {
    const ctx = this.ctx, b = this.buf.get(name);
    if (!ctx || !b || !this.master) return;
    const src = ctx.createBufferSource();
    src.buffer = b;
    src.playbackRate.value = (o.rate ?? 1) * (0.95 + Math.random() * 0.1);
    const g = ctx.createGain();
    g.gain.value = o.vol ?? 1;
    let node: AudioNode = src.connect(g);
    if (o.lowpass) {
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = o.lowpass;
      node = node.connect(f);
    }
    if (o.x !== undefined && o.z !== undefined) {
      const p = ctx.createPanner();
      p.panningModel = 'HRTF';
      p.distanceModel = 'inverse';
      p.refDistance = o.ref ?? 4;
      p.maxDistance = o.max ?? 80;
      p.rolloffFactor = 1.1;
      p.positionX.value = o.x; p.positionY.value = 1.2; p.positionZ.value = o.z;
      node = node.connect(p);
    }
    node.connect(this.sfx ?? this.master);
    src.start();
  }

  pick(prefix: string, n: number) { return `${prefix}_${Math.floor(Math.random() * n)}`; }

  // Bus de sortie (pour les sons synthétisés de soundscape.ts)
  bus(kind: 'sfx' | 'amb' | 'music' | 'ui'): AudioNode { return { sfx: this.sfx, amb: this.amb, music: this.music, ui: this.ui }[kind] ?? this.master!; }

  // Joue un son enregistré vers un nœud donné (sans placement dans le monde)
  playTo(name: string, out: AudioNode, o: PlayOpts = {}) {
    const ctx = this.ctx, b = this.buf.get(name);
    if (!ctx || !b) return;
    const src = ctx.createBufferSource();
    src.buffer = b;
    src.playbackRate.value = o.rate ?? 1;
    const g = ctx.createGain();
    g.gain.value = o.vol ?? 1;
    let node: AudioNode = src.connect(g);
    if (o.lowpass) { const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = o.lowpass; node = node.connect(f); }
    node.connect(out);
    src.start();
  }

  ambience() {
    if (!this.ctx || this.wind || !this.buf.get('wind') || !this.master) return;
    this.wind = this.ctx.createGain();
    this.wind.gain.value = 0.07;
    const src = this.ctx.createBufferSource();
    src.buffer = this.buf.get('wind')!;
    src.loop = true;
    src.connect(this.wind).connect(this.amb ?? this.master);
    src.start();
  }
}
